import { Fragment, useMemo } from 'react';
import {
  ChakraFlex,
  Divider,
  Tag,
  TagColor,
  Text,
  DateFormat,
} from '@equip.health/ui';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { useGetCarePlansByTreatmentEpisodeIdQuery } from '~/store/services/carePlan';
import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import Loading from '~/pages/loading';
import { formattedDateFromISO } from '~/lib/util/date.util';

const getStatusLabel = (status: string): string => {
  switch (status) {
    case CarePlanStatus.CLOSED:
      return 'Closed';
    case CarePlanStatus.VOID:
      return 'Void';
    default:
      return status;
  }
};

const getStatusColor = (status: string): TagColor => {
  switch (status) {
    case CarePlanStatus.CLOSED:
      return TagColor.gray;
    case CarePlanStatus.VOID:
      return TagColor.red;
    default:
      return TagColor.white;
  }
};

interface PreviousCarePlansProps {
  patientId: string;
}

export const PreviousCarePlans = ({ patientId }: PreviousCarePlansProps) => {
  const {
    data: patientTreatmentEpisodes,
    isLoading: isPatientTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId);

  const activeTreatmentEpisode = patientTreatmentEpisodes?.data.find(
    (episode) => episode.inTreatment,
  );

  const { data: carePlans, isLoading: isGetCarePlansLoading } =
    useGetCarePlansByTreatmentEpisodeIdQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      {
        skip: !activeTreatmentEpisode,
      },
    );

  const previousCarePlans = useMemo(() => {
    if (!carePlans) return [];

    return carePlans
      .filter(
        (carePlan) =>
          carePlan.status.code === CarePlanStatus.CLOSED ||
          carePlan.status.code === CarePlanStatus.VOID,
      )
      .toSorted(
        (carePlanA, carePlanB) =>
          new Date(carePlanB.startDate).getTime() -
          new Date(carePlanA.startDate).getTime(),
      );
  }, [carePlans]);

  const isLoading = isPatientTreatmentEpisodesLoading || isGetCarePlansLoading;
  const hasPreviousPathways = previousCarePlans.length > 0;

  if (isLoading) {
    return <Loading testId="previous-care-plans-loading" />;
  }

  if (!hasPreviousPathways) {
    return null;
  }

  return (
    <ChakraFlex
      flexDirection="column"
      gap="12px"
      marginTop="24px"
      paddingBottom="24px"
      paddingLeft="6px"
    >
      <Text marginBottom="12px" variant="h5">
        Past care plans
      </Text>

      {previousCarePlans.map((carePlan, index) => (
        <Fragment key={carePlan.id}>
          <ChakraFlex alignItems="center" gap="24px" height="41px">
            <ChakraFlex width="70px">
              {carePlan.treatmentEpisodeModality && (
                <Tag
                  color={TagColor.white}
                  id={`treatment-overview-level-tag-${carePlan.id}`}
                  isBorderRequired
                  value={carePlan.treatmentEpisodeModality.modality.label}
                />
              )}
            </ChakraFlex>
            <ChakraFlex direction="column" flex="1" gap="2px">
              <Text variant="bodyMedium">
                Care plan {previousCarePlans.length - index}
              </Text>
              <Text color="neutral.secondary" variant="caption">
                {carePlan.treatmentProgram.programName},{' '}
                {formattedDateFromISO(
                  carePlan.startDate,
                  DateFormat.monthDateYear,
                )}
                {carePlan.endDate &&
                  ` - ${formattedDateFromISO(carePlan.endDate, DateFormat.monthDateYear)}`}
              </Text>
            </ChakraFlex>
            <Tag
              color={getStatusColor(carePlan.status.code)}
              id={`previous-care-plan-status-tag-${carePlan.id}`}
              value={getStatusLabel(carePlan.status.code)}
            />
          </ChakraFlex>
          {index < previousCarePlans.length - 1 && (
            <Divider id={`previous-care-plan-divider-${carePlan.id}`} />
          )}
        </Fragment>
      ))}
    </ChakraFlex>
  );
};
